import React, { useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { Chip } from '@material-ui/core';
import { useGlobal } from '@rcg/ui-react';
import useDictionary from '../../hooks/useDictionary';
import { bookingProfileVar } from '../../operations/localState/reactiveVariables';
import Dictionary from '../../utils/dictionary';
import useStyles from './useStyles';
import FlexibilitySection from './components/FlexibilitySection';
import BaggageSection from './components/BaggageSection';
import AmenitiesSection from './components/AmenitiesSection';
import FareCardCTA from './components/FareCardCTA';
import getBrand from '../../utils/getBrand';
import { isFlexibilityService, isBaggageService } from '../../utils/inflightServices';

const isCelebrity = getBrand().name === 'celebrity';

const FareCard = ({ fare, isSelected = false, isReadOnly = false, onSelect }) => {
  const classes = useStyles();
  const getDictionary = useDictionary(Dictionary);
  const { renderContent } = useGlobal();
  const [showDetails, setShowDetails] = useState(!isReadOnly);

  const {
    brandName,
    isRefundable,
    isRefundableWithPenalty,
    isPaymentRequired,
    inflightServices = [],
    baggageInfo,
    wasPricePerPassenger,
    currency,
  } = fare;

  const isOnSale = Boolean(wasPricePerPassenger?.value);

  const valuePropChips = inflightServices.filter(
    (s) => s.isKeyFeature && (isFlexibilityService(s) || isBaggageService(s)),
  );

  const refundableLabel = (() => {
    if (isRefundableWithPenalty) return getDictionary(Dictionary.airRefundableWithPenaltyLabel.key);
    if (isRefundable) return getDictionary(Dictionary.airRefundableWithPenaltyLabel.key);
    return getDictionary(Dictionary.airNotRefundableLabel.key);
  })();

  // EXACTLY the gate CreditCardInformation applies when it builds the UPC payment types: the
  // courtesy_hold (Pay later) option is only offered when !isPaymentRequired AND the booking is
  // not in its final payment window (currentBooking.price.inFinal). Reading the same field here
  // means the fare badge and the payment step can never disagree. Browse has no currentBooking —
  // inFinal is undefined and the fare's own isPaymentRequired decides alone.
  const bookingProfile = useReactiveVar(bookingProfileVar);
  const mustPayNow = isPaymentRequired || Boolean(bookingProfile?.currentBooking?.price?.inFinal);

  const paymentLabel = mustPayNow
    ? getDictionary(Dictionary.airPayNowBadgeLabel.key)
    : getDictionary(Dictionary.airPayLaterBadgeLabel.key);

  const paymentBadgeClass = isCelebrity
    ? `${classes.payLabel} ${mustPayNow ? classes.payLabelNow : classes.payLabelLater}`
    : `${classes.payBadge} ${mustPayNow ? classes.payBadgeNow : classes.payBadgeLater}`;

  return (
    <div className={isSelected ? classes.fareCardSelected : classes.fareCard}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          {brandName && <p className={classes.brandName}>{brandName}</p>}
          <div className={classes.chipsRow}>
            {isOnSale && (
              <Chip
                label={getDictionary(Dictionary.airOnSaleLabel.key)}
                className={isCelebrity ? classes.onSaleChipCelebrity : classes.onSaleChip}
                size="small"
              />
            )}
            <Chip label={refundableLabel} size="small" className={classes.propChip} />
            {valuePropChips.map((s) => (
              <Chip
                key={s.key}
                label={renderContent(s.key, s.value)}
                size="small"
                className={classes.propChip}
              />
            ))}
          </div>
        </div>
        <div className={classes.headerRight}>
          <span className={paymentBadgeClass}>{paymentLabel}</span>
          {isReadOnly && (
            <button
              type="button"
              className={classes.toggleDetails}
              onClick={() => setShowDetails((v) => !v)}>
              {showDetails
                ? getDictionary(Dictionary.airHideDetailsLabel.key)
                : getDictionary(Dictionary.airViewDetailsLabel.key)}
            </button>
          )}
        </div>
      </div>

      {showDetails && (
        <div className={classes.body}>
          <div className={classes.leftCol}>
            <FlexibilitySection inflightServices={inflightServices} />
            <BaggageSection baggageInfo={baggageInfo} currency={currency} />
          </div>
          <div className={classes.rightCol}>
            <AmenitiesSection inflightServices={inflightServices} />
          </div>
          {!isReadOnly && (
            <div className={classes.ctaCol}>
              <FareCardCTA
                fare={fare}
                isSelected={isSelected}
                onSelect={onSelect}
                paymentLabel={paymentLabel}
                isCelebrity={isCelebrity}
                payNow={mustPayNow}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FareCard;
